import React from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Sparkles, RefreshCw, MapPin, Globe, Tag, Info, Mail, Check, ExternalLink, BookOpen } from "lucide-react";

const RESEARCH_SOURCES = [
  { label: "The Bookseller",        url: "https://www.thebookseller.com/" },
  { label: "PM · Fantasy deals",    url: "https://www.publishersmarketplace.com/dealmaker/dealsearch.cgi?category=Sci-Fi/Fantasy" },
  { label: "#MSWL on Bluesky",      url: "https://bsky.app/search?q=%23MSWL" },
  { label: "#MSWL Fantasy",         url: "https://bsky.app/search?q=%23MSWL+fantasy" },
  { label: "Writers & Artists news", url: "https://www.writersandartists.co.uk/news" },
  { label: "Mark Lawrence's SPFBO",  url: "https://mark---lawrence.blogspot.com/p/spfbo.html" },
  { label: "SFWA market news",       url: "https://www.sfwa.org/category/news/" },
];

const CATEGORY_COLOURS = {
  "MARKET TREND":      "var(--elle-amber)",
  "AGENT INSIGHT":     "var(--elle-moss)",
  "SUBMISSION WINDOW": "var(--elle-amber-bright)",
  "SUCCESS STORY":     "var(--elle-moss)",
  "INDUSTRY SHIFT":    "var(--elle-oxblood-bright)",
};

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-GB",
    { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function DailyBrief() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [context, setContext] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  // Track which briefs are already in an open/draft newsletter issue so the
  // "Add to newsletter" button can show ✓ Added instead of Add.
  const [draftIssue, setDraftIssue] = React.useState(null);
  const [adding, setAdding] = React.useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/brief/today");
      setData(data);
    } catch (e) {
      toast.error("Couldn't load today's brief.");
    } finally { setLoading(false); }
  };
  const loadDraftIssue = async () => {
    try {
      const { data } = await api.get("/newsletter/issues");
      // Find the most recent DRAFT (not yet sent). If multiple, use newest.
      const draft = (data || []).find(i => (i.status || "draft") === "draft") || null;
      setDraftIssue(draft);
    } catch {
      /* ignore — newsletter is optional */
    }
  };
  React.useEffect(() => { load(); loadDraftIssue(); }, []);

  const addBriefToNewsletter = async (brief) => {
    setAdding(brief.id);
    try {
      let issue = draftIssue;
      // If no open draft, create one with a sensible default subject.
      if (!issue) {
        const today = new Date().toLocaleDateString("en-GB",
          { day: "numeric", month: "long" });
        const { data } = await api.post("/newsletter/issues", {
          subject: `From the Workshop — ${today}`,
          intro_markdown: "",
          outro_markdown: "",
          brief_ids: [brief.id],
          custom_sections: [],
        });
        issue = data;
        toast.success("Draft issue created and brief added.");
      } else {
        const ids = issue.brief_ids || [];
        if (ids.includes(brief.id)) {
          toast.info("Already in the draft.");
          return;
        }
        const { data } = await api.put(`/newsletter/issues/${issue.slug}`, {
          brief_ids: [...ids, brief.id],
        });
        issue = data;
        toast.success("Added to the current draft.");
      }
      setDraftIssue(issue);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add to newsletter.");
    } finally { setAdding(null); }
  };

  const regenerate = async (withContext) => {
    setBusy(true);
    try {
      const payload = withContext && context.trim() ? { context: context.trim() } : {};
      const { data } = await api.post("/brief/generate", payload);
      setData(data);
      if (withContext) {
        toast.success(`Brief regenerated with your context · ${data.briefs?.length || 0} brief(s).`);
        setContext("");
      } else {
        toast.success(`Baseline brief generated · ${data.briefs?.length || 0} brief(s).`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regeneration failed.");
    } finally { setBusy(false); }
  };

  const ukCount   = (data?.briefs || []).filter(b => b.region === "UK").length;
  const globCount = (data?.briefs || []).filter(b => b.region === "GLOBAL").length;

  return (
    <div className="max-w-5xl" data-testid="daily-brief-page">
      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Literary Agent</div>
          <h1 className="font-display text-5xl mt-2" style={{ color: "var(--elle-ink)" }}>
            Daily Brief
          </h1>
          <p className="mt-2 text-sm max-w-xl" style={{ color: "var(--elle-ink-dim)" }}>
            Six things the publishing world thinks you should know today —
            filtered through an agent who won't sell you hype.
          </p>
        </div>
        <div className="text-right text-xs font-mono-elle" style={{ color: "var(--elle-ink-faint)" }}>
          {data?.generated_at && (
            <div>Generated {formatDate(data.generated_at)}</div>
          )}
          {data && (
            <div className="mt-1">
              {data.baseline
                ? <><Info size={11} className="inline mr-1"/>Baseline · no live web</>
                : <><Sparkles size={11} className="inline mr-1"/>Folded in your context</>}
            </div>
          )}
          {draftIssue && (
            <div className="mt-2">
              <Link to="/dashboard/newsletter"
                    className="inline-flex items-center gap-1 underline"
                    style={{ color: "var(--elle-amber)" }}
                    data-testid="open-newsletter-draft">
                <Mail size={11}/> Open draft · {(draftIssue.brief_ids || []).length} brief(s)
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Research sources quick-open — click to open in a new tab, grab a headline, paste below */}
      <section className="elle-surface p-5 rounded-sm mb-6" data-testid="brief-research-sources">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
          <div>
            <div className="overline flex items-center gap-2">
              <BookOpen size={13}/> Today's research spread
            </div>
            <p className="text-xs mt-1" style={{ color: "var(--elle-ink-dim)" }}>
              Open a few in new tabs, skim, copy what's interesting, paste below. The agent cannot
              browse; you are its eyes for today.
            </p>
          </div>
          <button
            data-testid="open-all-sources"
            onClick={() => {
              RESEARCH_SOURCES.forEach(s => window.open(s.url, "_blank", "noopener,noreferrer"));
              toast.success(`Opened ${RESEARCH_SOURCES.length} research sources in new tabs.`);
            }}
            className="text-xs px-3 py-1.5 rounded-sm font-mono-elle hover:text-[var(--elle-amber)] transition-colors"
            style={{ border: "1px solid var(--elle-border)", color: "var(--elle-ink-dim)" }}>
            <ExternalLink size={12} className="inline mr-1.5 -mt-0.5"/> Open all in new tabs
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {RESEARCH_SOURCES.map(s => (
            <a key={s.url}
               href={s.url}
               target="_blank"
               rel="noopener noreferrer"
               data-testid={`source-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
               className="text-xs font-mono-elle px-3 py-1.5 rounded-sm transition-colors hover:text-[var(--elle-amber)] inline-flex items-center gap-1.5"
               style={{ border: "1px solid var(--elle-border)", color: "var(--elle-ink-dim)" }}>
              {s.label} <ExternalLink size={10}/>
            </a>
          ))}
        </div>
      </section>

      {/* Context paste box */}
      <section className="elle-surface p-6 rounded-sm mb-8" data-testid="brief-context-section">
        <label className="overline block mb-2 flex items-center gap-2">
          <Sparkles size={13}/> Paste today's real context (optional)
        </label>
        <p className="text-xs mb-3" style={{ color: "var(--elle-ink-dim)" }}>
          Saw a Bookseller headline, an agent tweet, a Publishers Marketplace deal?
          Paste it below (URLs, quotes, raw text — anything) and the agent will
          curate today's brief around it. Leave empty to regenerate from baseline
          knowledge.
        </p>
        <Textarea
          data-testid="brief-context-input"
          value={context}
          onChange={(e)=>setContext(e.target.value)}
          placeholder={"Example:\nBookseller: Faber launches new fantasy imprint \"Ember\" — acquiring now.\nAgent tweet: @emilyswitzer looking for cozy fantasy with queer leads, word count 80-100k.\n..."}
          className="min-h-[140px] font-mono-elle text-sm"
        />
        <div className="mt-4 flex flex-wrap gap-2">
          <Button data-testid="brief-regenerate-with-context"
                  onClick={()=>regenerate(true)}
                  disabled={busy || !context.trim()}
                  className="btn-amber rounded-sm">
            <Sparkles size={14} className="mr-2"/>
            {busy ? "Curating…" : "Regenerate with this context"}
          </Button>
          <Button data-testid="brief-regenerate-baseline"
                  onClick={()=>regenerate(false)}
                  disabled={busy}
                  variant="outline" className="btn-ghost rounded-sm">
            <RefreshCw size={14} className="mr-2"/>
            {busy ? "…" : "Regenerate baseline"}
          </Button>
        </div>
      </section>

      {/* Briefs list */}
      {loading ? (
        <div className="text-sm font-mono-elle" style={{ color: "var(--elle-ink-dim)" }}>
          Lighting the lamp…
        </div>
      ) : !data?.briefs || data.briefs.length === 0 ? (
        <div className="elle-surface p-8 rounded-sm text-center"
             style={{ color: "var(--elle-ink-dim)" }}>
          <p className="text-sm">No brief yet today.</p>
          {data?.gap_note ? (
            <p className="text-xs mt-3 italic" data-testid="brief-gap-note"
               style={{ color: "var(--elle-amber)" }}>
              {data.gap_note}
            </p>
          ) : null}
          <p className="text-xs mt-2" style={{ color: "var(--elle-ink-faint)" }}>
            The scheduler runs at 07:30 UTC. Or click "Regenerate baseline" above to build one now.
          </p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="brief-list">
          {data.gap_note && (
            <div className="p-4 rounded-sm text-sm"
                 data-testid="brief-gap-note"
                 style={{ border: "1px solid var(--elle-amber)",
                          background: "rgba(197,160,89,0.06)",
                          color: "var(--elle-ink)" }}>
              <div className="overline mb-1" style={{ color: "var(--elle-amber-bright)" }}>
                agent's note
              </div>
              {data.gap_note}
            </div>
          )}
          {data.briefs.map((b, i) => {
            const catColour = CATEGORY_COLOURS[b.category] || "var(--elle-ink-dim)";
            return (
              <article key={b.id || i}
                       data-testid={`brief-card-${i}`}
                       className="elle-surface p-6 rounded-sm border-l-2 transition-colors hover:bg-[var(--elle-surface-2)]"
                       style={{ borderLeftColor: catColour }}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <h2 className="font-display text-2xl leading-tight flex-1 min-w-0"
                      style={{ color: "var(--elle-ink)" }}>
                    {b.headline}
                  </h2>
                  <div className="flex items-center gap-2 text-xs font-mono-elle shrink-0">
                    <span className="inline-flex items-center gap-1"
                          style={{ color: b.region === "UK" ? "var(--elle-amber)" : "var(--elle-ink-dim)" }}>
                      {b.region === "UK" ? <MapPin size={11}/> : <Globe size={11}/>} {b.region}
                    </span>
                    <span className="inline-flex items-center gap-1"
                          style={{ color: catColour }}>
                      <Tag size={11}/> {b.category}
                    </span>
                  </div>
                </div>
                {b.whats_happening && (
                  <div className="mt-4">
                    <div className="overline mb-1">What's happening</div>
                    <p className="text-sm" style={{ color: "var(--elle-ink)" }}>
                      {b.whats_happening}
                    </p>
                  </div>
                )}
                {b.why_it_matters && (
                  <div className="mt-3">
                    <div className="overline mb-1">Why it matters for you</div>
                    <p className="text-sm" style={{ color: "var(--elle-ink-dim)" }}>
                      {b.why_it_matters}
                    </p>
                  </div>
                )}
                {b.what_to_do && (
                  <div className="mt-3">
                    <div className="overline mb-1">What to do</div>
                    <p className="text-sm" style={{ color: "var(--elle-ink-dim)" }}>
                      {b.what_to_do}
                    </p>
                  </div>
                )}
                <div className="mt-5 pt-4 border-t flex items-center justify-between gap-3"
                     style={{ borderColor: "var(--elle-line)" }}>
                  <div className="text-xs font-mono-elle" style={{ color: "var(--elle-ink-faint)" }}>
                    {(draftIssue?.brief_ids || []).includes(b.id)
                      ? `In current draft · "${draftIssue.subject}"`
                      : draftIssue ? `Draft open: "${draftIssue.subject}"` : "No open newsletter draft."}
                  </div>
                  {(draftIssue?.brief_ids || []).includes(b.id) ? (
                    <span className="text-xs font-mono-elle inline-flex items-center gap-1"
                          style={{ color: "var(--elle-moss)" }}
                          data-testid={`brief-added-${i}`}>
                      <Check size={12}/> Added
                    </span>
                  ) : (
                    <Button size="sm" variant="outline"
                            className="btn-ghost rounded-sm"
                            onClick={()=>addBriefToNewsletter(b)}
                            disabled={adding === b.id}
                            data-testid={`brief-add-to-newsletter-${i}`}>
                      <Mail size={12} className="mr-1"/>
                      {adding === b.id ? "Adding…" : (draftIssue ? "Add to draft" : "Start newsletter draft")}
                    </Button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* Footer summary */}
      {data?.briefs?.length > 0 && (
        <div className="mt-8 pt-6 border-t text-xs font-mono-elle"
             style={{ borderColor: "var(--elle-border)", color: "var(--elle-ink-faint)" }}>
          {data.briefs.length} stor{data.briefs.length === 1 ? "y" : "ies"} ·
          {" "}~{Math.max(2, data.briefs.length)} min read ·
          {" "}{ukCount} UK · {globCount} Global
          {data.baseline && (
            <>  ·  <span style={{ color: "var(--elle-ink-dim)" }}>
              baseline (paste context above for today-specific curation)
            </span></>
          )}
        </div>
      )}
    </div>
  );
}
